import React, { useState } from 'react';
import { ProgressBar, Card, Row, Col, Button } from 'react-bootstrap';
import { completeCyclePhase, registerHarvest, updateHarvest, finishCycle, abandonCycle } from '../../../services/v2/plantingCycleService';
import ConfirmPhaseModal from '../../../shared/components/confirmPhaseModal/ConfirmPhaseModal';
import RegisterHarvestModal from './RegisterHarvestModal';
import FinishCycleModal from './FinishCycleModal';
import AbandonCycleModal from './AbandonCycleModal';

const CycleStatus = ({ plantingCycle, onCycleUpdated, readOnly = false }) => {
    const [forcingStage, setForcingStage] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showHarvestModal, setShowHarvestModal] = useState(false);
    const [editingHarvest, setEditingHarvest] = useState(null);
    const [registeringHarvest, setRegisteringHarvest] = useState(false);
    const [showFinishModal, setShowFinishModal] = useState(false);
    const [finishingCycle, setFinishingCycle] = useState(false);
    const [showAbandonModal, setShowAbandonModal] = useState(false);
    const [abandoningCycle, setAbandoningCycle] = useState(false);

    const closureDateLabel = (type) => {
        switch (type) {
            case 'HARVEST_END': return 'Fin de cosecha';
            case 'HARVEST_PEAK': return 'Pico de cosecha';
            case 'HARVEST_START': return 'Inicio de cosecha';
            case 'ADMIN_CLOSURE': return 'Abandonado el';
            default: return 'Cierre';
        }
    };

    const refetch = () => {
        if (onCycleUpdated) {
            onCycleUpdated();
        } else {
            window.location.reload();
        }
    };

    const handleForceNextStage = async () => {
        setForcingStage(true);
        try {
            await completeCyclePhase(plantingCycle.id, plantingCycle.currentPhaseLogId);
            setShowConfirmModal(false);
            refetch();
        } catch (error) {
            console.error('Error forcing next stage', error);
            alert('Error al forzar la siguiente etapa.');
        } finally {
            setForcingStage(false);
        }
    };

    const getErrorMessage = (error, fallback) => {
        const data = error.response?.data;
        return typeof data === 'string' && data.length > 0 ? data : fallback;
    };

    const handleSaveHarvest = async (harvestData) => {
        setRegisteringHarvest(true);
        try {
            if (editingHarvest) {
                await updateHarvest(plantingCycle.id, editingHarvest.id, harvestData);
            } else {
                await registerHarvest(plantingCycle.id, harvestData);
            }
            setShowHarvestModal(false);
            setEditingHarvest(null);
            refetch();
        } catch (error) {
            console.error('Error saving harvest', error);
            alert(getErrorMessage(error, 'Error al guardar la cosecha.'));
        } finally {
            setRegisteringHarvest(false);
        }
    };

    const handleFinishCycle = async (finishData) => {
        setFinishingCycle(true);
        try {
            await finishCycle(plantingCycle.id, finishData);
            setShowFinishModal(false);
            refetch();
        } catch (error) {
            console.error('Error finishing cycle', error);
            alert(getErrorMessage(error, 'Error al finalizar el cultivo.'));
        } finally {
            setFinishingCycle(false);
        }
    };

    const handleAbandonCycle = async (abandonData) => {
        setAbandoningCycle(true);
        try {
            await abandonCycle(plantingCycle.id, abandonData);
            setShowAbandonModal(false);
            refetch();
        } catch (error) {
            console.error('Error abandoning cycle', error);
            alert(getErrorMessage(error, 'Error al abandonar el cultivo.'));
        } finally {
            setAbandoningCycle(false);
        }
    };

    const status = plantingCycle.status;
    const isMultiple = plantingCycle.harvestType === 'MULTIPLE';
    const harvests = plantingCycle.harvests || [];
    const canRegisterHarvest = status === 'ACTIVE' || (status === 'HARVESTING' && isMultiple);
    // Cosecha única: una vez en HARVESTING ya no se registra una cosecha nueva, sino que se
    // completa la existente (pico/fin) si todavía le falta alguna de esas dos fechas.
    const singleHarvest = !isMultiple ? harvests[0] : null;
    const canCompleteSingleHarvest = status === 'HARVESTING' && !isMultiple && singleHarvest
        && (singleHarvest.harvestPeakDate == null || singleHarvest.harvestEndDate == null);
    const canFinish = status === 'HARVESTING';
    const canAbandon = status === 'ACTIVE' || status === 'HARVESTING';
    const showClosureNotes = (status === 'FINISHED' || status === 'ABANDONED')
        && (!!plantingCycle.notes || !!plantingCycle.finishedDate || !!plantingCycle.closureReferenceDate);
    // COMPLETED nunca tiene notas del productor (el cierre lo hace el sistema, no una
    // acción manual), así que se muestra aparte con un mensaje fijo en vez de reusar
    // plantingCycle.notes.
    const showAutoCompletedNotice = status === 'COMPLETED';

    const deviation = plantingCycle.cycleDeviation;
    const daysDeviation = deviation?.daysDeviation ?? null;
    const toleranceAccumulated = deviation?.toleranceAccumulated ?? 1;
    const isWithinTolerance = deviation?.isWithinTolerance ?? true;

    const CLAMP = Math.max(toleranceAccumulated * 3, 5);
    const gaugePosition = daysDeviation !== null
        ? Math.min(95, Math.max(5, 50 - (daysDeviation / CLAMP) * 45))
        : 50;

    const getStatusLabel = () => {
        if (daysDeviation === null) return { label: 'SIN DATOS', color: 'text-gray-400' };
        if (isWithinTolerance) return { label: 'A TIEMPO', color: 'text-green-500' };
        if (daysDeviation > 0) return { label: 'ATRASADO', color: 'text-red-500' };
        return { label: 'ADELANTADO', color: 'text-blue-500' };
    };
    const statusInfo = getStatusLabel();

    const days = plantingCycle.daysCount || 0;
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    const timeLabel = weeks > 0
        ? `${weeks} sem ${remainingDays > 0 ? `y ${remainingDays} d` : ''}`
        : `${days} días`;

    const sortedPhases = (plantingCycle.phaseLogs || [])
        .slice()
        .sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));
    const lastPhase = sortedPhases.at(-1);
    // Cuando la última fase ya está COMPLETED, plantingCycle.estimatedHarvestDate deja de ser
    // una proyección y pasa a ser la fecha real de cierre de esa fase — el label del front debe
    // reflejar ese cambio en vez de seguir diciendo "estimación".
    const lastPhaseCompleted = lastPhase?.status === 'COMPLETED';
    const harvestEst = plantingCycle.estimatedHarvestDate || lastPhase?.estimatedCompletionDate;
    const harvestEstMin = lastPhase?.estimatedCompletionDateMin;
    const harvestEstMax = lastPhase?.estimatedCompletionDateMax;

    const activePhase = sortedPhases.find((p) => p.status === 'IN_PROGRESS');
    const activeMetric = activePhase?.completionMetric || 'GDD';

    const renderPrimaryMetricCard = () => {
        if (activeMetric === 'CHILL') {
            const target = activePhase?.targetChill || 0;
            const accumulated = plantingCycle.chillAccumulated || 0;
            const pct = target > 0 ? Math.min(100, Math.round((accumulated / target) * 100)) : 0;
            return (
                <>
                    <div className="flex justify-between items-start mb-4">
                        <h6 className="text-[11px] uppercase font-bold text-gray-400 tracking-widest">Frío Acumulado (Chill Portions)</h6>
                        <span className="material-icons text-gray-300 text-sm">ac_unit</span>
                    </div>

                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-black text-gray-800">{Math.round(accumulated)}</span>
                        <span className="text-lg font-bold text-gray-400">CHP</span>
                    </div>

                    <p className="text-xs text-gray-500 mb-6">de {Math.round(target)} CHP necesarias para romper dormancia</p>

                    <div className="space-y-2">
                        <div className="flex justify-between text-xs font-bold">
                            <span className="text-gray-400">Progreso Dormancia</span>
                            <span className="text-blue-600">{pct}%</span>
                        </div>
                        <ProgressBar now={pct} variant="info" className="h-2 rounded-full bg-gray-100" />
                        <div className="flex justify-between text-[9px] text-gray-300 font-bold uppercase">
                            <span>0</span>
                            <span>{Math.round(target)}</span>
                        </div>
                    </div>
                </>
            );
        }

        if (activeMetric === 'DAYS' || activeMetric === 'MANUAL') {
            const targetDays = activePhase?.expectedDaysSnapshot;
            return (
                <>
                    <div className="flex justify-between items-start mb-4">
                        <h6 className="text-[11px] uppercase font-bold text-gray-400 tracking-widest">
                            {activeMetric === 'MANUAL' ? 'Etapa Manual' : 'Avance por Días'}
                        </h6>
                        <span className="material-icons text-gray-300 text-sm">event</span>
                    </div>

                    <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-5xl font-black text-gray-800">{plantingCycle.daysCount || 0}</span>
                        <span className="text-lg font-bold text-gray-400">días</span>
                    </div>

                    <p className="text-xs text-gray-500 mb-6">
                        {activeMetric === 'MANUAL'
                            ? 'Esta etapa se completa manualmente con "Forzar Etapa".'
                            : targetDays ? `de ${targetDays} días esperados para esta etapa` : 'Sin objetivo de días definido'}
                    </p>
                </>
            );
        }

        // Una vez completada la última etapa, el GDD acumulado deja de crecer indefinidamente y se
        // congela en el valor real del Inicio Pre-pico: el gddAtHarvestStart de la primera cosecha
        // registrada (fuente más confiable, ligada a la fecha real que la persona ingresó). Si aún
        // no se registró ninguna cosecha, se aproxima sumando el gddAtCompletion de cada fase
        // (gddAtCompletion es solo el delta de esa fase, no el total del ciclo).
        const firstHarvest = harvests.length > 0
            ? harvests.slice().sort((a, b) => a.harvestStartDate.localeCompare(b.harvestStartDate))[0]
            : null;
        const gddValue = lastPhaseCompleted
            ? (firstHarvest?.gddAtHarvestStart != null
                ? firstHarvest.gddAtHarvestStart
                : sortedPhases.reduce((sum, p) => sum + (p.gddAtCompletion || 0), 0))
            : (plantingCycle.gddAccumulated || 0);

        return (
            <>
                <div className="flex justify-between items-start mb-4">
                    <h6 className="text-[11px] uppercase font-bold text-gray-400 tracking-widest">Grados Día Acumulados (GDD)</h6>
                    <span className="material-icons text-gray-300 text-sm">info_outline</span>
                </div>

                <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-5xl font-black text-gray-800">{Math.round(gddValue)}</span>
                    <span className="text-lg font-bold text-gray-400">GDD</span>
                </div>

                <p className="text-xs text-gray-500 mb-6">de {Math.round(plantingCycle.gddTarget || 0)} GDD necesarios para inicio de cosecha (Pre-pico)</p>

                <div className="space-y-2">
                    <div className="flex justify-between text-xs font-bold">
                        <span className="text-gray-400">Progreso Total</span>
                        <span className="text-green-600">{plantingCycle.progress || 0}%</span>
                    </div>
                    <ProgressBar now={plantingCycle.progress || 0} variant="success" className="h-2 rounded-full bg-gray-100" />
                    <div className="flex justify-between text-[9px] text-gray-300 font-bold uppercase">
                        <span>0</span>
                        <span>{Math.round(plantingCycle.gddTarget || 0)}</span>
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="space-y-6">
            {/* 1. Header Summary Bar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-wrap justify-between items-center gap-6">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 rounded-lg">
                        <span className="material-icons-outlined text-indigo-500">inventory_2</span>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Variedad</div>
                        <div className="text-sm font-bold text-gray-800">{plantingCycle.varietyName || 'N/A'}</div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-50 rounded-lg">
                        <span className="material-icons-outlined text-gray-400">calendar_today</span>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Inicio siembra</div>
                        <div className="text-sm font-bold text-gray-800">{plantingCycle.startDate}</div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                        <span className="material-icons-outlined text-green-500">schedule</span>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Tiempo de cultivo</div>
                        <div className="text-sm font-bold text-gray-800">{timeLabel}</div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-50 rounded-lg">
                        <span className="material-icons-outlined text-orange-500">psychology</span>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Etapa Actual</div>
                        <div className="text-sm font-bold text-gray-800 truncate max-w-[150px]">{plantingCycle.currentStage || 'N/A'}</div>
                    </div>
                </div>

                {readOnly ? (
                    <span className="text-xs text-gray-500 flex items-center">
                        <span className="material-icons-outlined text-sm mr-1">visibility</span>
                        Solo lectura
                    </span>
                ) : (
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="warning"
                        className="rounded-lg font-bold flex items-center shadow-sm px-4"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={!plantingCycle.currentPhaseLogId}
                    >
                        <span className="material-icons text-sm mr-2">fast_forward</span>
                        Forzar Etapa
                    </Button>

                    {canRegisterHarvest && (
                        <Button
                            variant="success"
                            className="rounded-lg font-bold flex items-center shadow-sm px-4"
                            onClick={() => { setEditingHarvest(null); setShowHarvestModal(true); }}
                        >
                            <span className="material-icons text-sm mr-2">agriculture</span>
                            Registrar Cosecha
                        </Button>
                    )}

                    {canCompleteSingleHarvest && (
                        <Button
                            variant="success"
                            className="rounded-lg font-bold flex items-center shadow-sm px-4"
                            onClick={() => { setEditingHarvest(singleHarvest); setShowHarvestModal(true); }}
                        >
                            <span className="material-icons text-sm mr-2">edit_calendar</span>
                            Completar Cosecha
                        </Button>
                    )}

                    {canFinish && (
                        <Button
                            variant="primary"
                            className="rounded-lg font-bold flex items-center shadow-sm px-4"
                            onClick={() => setShowFinishModal(true)}
                        >
                            <span className="material-icons text-sm mr-2">task_alt</span>
                            Finalizar Cultivo
                        </Button>
                    )}

                    {canAbandon && (
                        <Button
                            variant="outline-danger"
                            className="rounded-lg font-bold flex items-center shadow-sm px-4"
                            onClick={() => setShowAbandonModal(true)}
                        >
                            <span className="material-icons text-sm mr-2">cancel</span>
                            Abandonar
                        </Button>
                    )}
                </div>
                )}
            </div>

            {/* 1b. Closure notes (finished/abandoned reason) */}
            {showClosureNotes && (
                <div className={`p-4 rounded-xl border flex items-start gap-3 ${
                    status === 'ABANDONED'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-indigo-50 border-indigo-100'
                }`}>
                    <span className={`material-icons ${status === 'ABANDONED' ? 'text-red-500' : 'text-indigo-500'}`}>
                        {status === 'ABANDONED' ? 'cancel' : 'task_alt'}
                    </span>
                    <div>
                        <div className={`text-[10px] uppercase font-bold tracking-wider mb-1 ${
                            status === 'ABANDONED' ? 'text-red-500' : 'text-indigo-500'
                        }`}>
                            {status === 'ABANDONED' ? 'Razón del abandono' : 'Notas de finalización'}
                        </div>
                        <div className="text-sm text-gray-700">{plantingCycle.notes}</div>
                        {plantingCycle.closureReferenceDate && (
                            <div className={`text-xs font-bold mt-1 ${status === 'ABANDONED' ? 'text-red-500' : 'text-indigo-500'}`}>
                                {closureDateLabel(plantingCycle.closureReferenceDateType)}: {plantingCycle.closureReferenceDate}
                            </div>
                        )}
                        {plantingCycle.finishedDate && plantingCycle.finishedDate !== plantingCycle.closureReferenceDate && (
                            <div className="text-xs text-gray-400 font-bold mt-1">
                                Registrado en el sistema el: {plantingCycle.finishedDate}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 1c. Auto-closure notice (COMPLETED: cierre automático del sistema, sin cosecha) */}
            {showAutoCompletedNotice && (
                <div className="p-4 rounded-xl border flex items-start gap-3 bg-slate-50 border-slate-200">
                    <span className="material-icons text-slate-500">smart_toy</span>
                    <div>
                        <div className="text-[10px] uppercase font-bold tracking-wider mb-1 text-slate-500">
                            Cerrado automáticamente
                        </div>
                        <div className="text-sm text-gray-700">
                            Se completaron todas las fases de crecimiento y no se registró ninguna cosecha, así que
                            el sistema dejó de calcular este ciclo.
                        </div>
                        {plantingCycle.closureReferenceDate && (
                            <div className="text-xs font-bold mt-1 text-slate-500">
                                Fin de la última fase: {plantingCycle.closureReferenceDate}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 2. Main Dashboard Cards */}
            <Row className="g-4">
                {/* GDD Card */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-xl h-100 overflow-hidden">
                        <Card.Body className="p-6">
                            {renderPrimaryMetricCard()}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Status Gauge Card */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-xl h-100">
                        <Card.Body className="p-6">
                            <div className="flex justify-between items-start mb-1">
                                <h6 className="text-[11px] uppercase font-bold text-gray-400 tracking-widest">Desviación del Ciclo</h6>
                            </div>
                            <p className="text-[10px] text-gray-400 mb-3">
                                Diferencia en días (real vs. esperado) acumulada fase por fase, frente a la tolerancia total.
                            </p>

                            <div className="flex flex-col items-center justify-center h-full py-2">
                                <div className={`text-2xl font-black mb-2 ${statusInfo.color}`}>
                                    {statusInfo.label}
                                </div>

                                {deviation && (
                                    <div className="text-xs text-gray-400 font-bold mb-3 text-center">
                                        Días: <span className={daysDeviation <= 0 ? 'text-blue-500' : 'text-red-500'}>
                                            {daysDeviation >= 0 ? '+' : ''}{daysDeviation}d
                                        </span>
                                        <span className="text-gray-300 mx-1">·</span>
                                        Tolerancia: <span className="text-gray-500">±{toleranceAccumulated}d</span>
                                    </div>
                                )}

                                <div className="w-full mt-2 px-2">
                                    <div className="flex h-3 rounded-full overflow-hidden mb-2 shadow-inner">
                                        <div className="bg-red-400 w-1/4"></div>
                                        <div className="bg-yellow-400 w-1/4"></div>
                                        <div className="bg-green-500 w-1/4"></div>
                                        <div className="bg-blue-400 w-1/4"></div>
                                    </div>
                                    <div className="relative w-full h-4">
                                        <div
                                            className="absolute top-[-15px] transition-all duration-1000"
                                            style={{ left: `${gaugePosition}%`, transform: 'translateX(-50%)' }}
                                        >
                                            <span className="material-icons text-gray-800 text-xl">arrow_drop_up</span>
                                        </div>
                                    </div>
                                    <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase mt-2">
                                        <span className="text-red-400">Atrasado</span>
                                        <span className="text-green-500">A tiempo</span>
                                        <span className="text-blue-400">Adelantado</span>
                                    </div>
                                </div>

                                {!isWithinTolerance && (
                                    <div className="mt-3 px-2 py-1 bg-red-50 border border-red-200 rounded text-[9px] font-black text-red-500 uppercase">
                                        ⚠ Fuera del rango de tolerancia
                                    </div>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Harvest Estimate Card */}
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-xl h-100 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white">
                        <Card.Body className="p-6 flex flex-col justify-between">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="material-icons text-indigo-200">{lastPhaseCompleted ? 'check_circle' : 'lightbulb'}</span>
                                    <h6 className="text-[11px] uppercase font-bold text-indigo-100 tracking-widest mb-0">
                                        {lastPhaseCompleted ? 'Inicio de Cosecha' : 'Estimación de Inicio de Cosecha'}
                                    </h6>
                                </div>
                                <p className="text-[10px] text-indigo-200/80 mb-4">
                                    {lastPhaseCompleted
                                        ? 'El modelo fenológico indica que el cultivo alcanzó el punto de inicio de cosecha (pre-pico) en esta fecha (no es una fecha de cosecha registrada).'
                                        : 'Proyectada según el ritmo de GDD/Chill de la última fase del ciclo.'}
                                </p>

                                {harvestEst ? (
                                    <div className="space-y-2">
                                        <div>
                                            <div className="text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1">
                                                {lastPhaseCompleted ? 'Punto de inicio de cosecha (pre-pico modelo)' : 'Fecha estimada'}
                                            </div>
                                            <div className="text-xl font-black text-white">{harvestEst}</div>
                                        </div>

                                        {!lastPhaseCompleted && harvestEstMin && harvestEstMax && (
                                            <div>
                                                <div className="text-[10px] uppercase font-bold text-indigo-300 tracking-wider mb-1">Rango</div>
                                                <div className="text-sm font-bold text-indigo-100">
                                                    {harvestEstMin} — {harvestEstMax}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-sm font-medium leading-relaxed text-indigo-100">
                                        Sin estimación disponible aún.
                                    </p>
                                )}
                            </div>

                            <div className="mt-4 pt-4 border-t border-indigo-500/30 space-y-1">
                                {plantingCycle.plannedHarvestDate && (
                                    <div className="flex items-center justify-between text-xs font-bold">
                                        <span className="text-indigo-300">Objetivo original</span>
                                        <span className="text-indigo-200">{plantingCycle.plannedHarvestDate}</span>
                                    </div>
                                )}
                                {plantingCycle.realHarvestDate && (
                                    <div className="flex items-center justify-between text-xs font-bold">
                                        <span className="text-indigo-300">Última cosecha real</span>
                                        <span className="text-indigo-200">{plantingCycle.realHarvestDate}</span>
                                    </div>
                                )}
                                <div className="flex items-center justify-between text-xs font-bold">
                                    <span className="text-indigo-300">Variedad</span>
                                    <span className="text-indigo-200 uppercase">{plantingCycle.varietyName || '---'}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* 3. Fases Fenológicas — versión compacta para mobile */}
            <Card className="border-0 shadow-sm rounded-xl overflow-hidden">
                <Card.Header className="bg-white border-b border-gray-100 py-4 flex justify-between items-center">
                    <h6 className="mb-0 text-gray-800 font-bold uppercase text-[11px] tracking-widest">Fases Fenológicas</h6>
                    <span className="text-[10px] text-gray-400 font-bold">Etapa Actual: {plantingCycle.currentStage}</span>
                </Card.Header>
                <Card.Body className="p-6">
                    <div className="flex items-start overflow-x-auto pb-4 gap-4 scrollbar-hide">
                        {sortedPhases.map((phase, index) => {
                            const isCurrent = phase.status === 'IN_PROGRESS';
                            const isCompleted = phase.status === 'COMPLETED';
                            const isSkipped = phase.status === 'SKIPPED';
                            const isFuture = !isCurrent && !isCompleted && !isSkipped;

                            return (
                                <div key={phase.id} className={`flex flex-col items-center min-w-[130px] relative transition-opacity ${isFuture ? 'opacity-60 hover:opacity-100' : isSkipped ? 'opacity-70' : 'opacity-100'}`}>
                                    {/* Línea conectora */}
                                    {index < sortedPhases.length - 1 && (
                                        <div className={`absolute top-4 left-1/2 w-full h-[2px] -z-0 ${isCompleted ? 'bg-green-500' : isSkipped ? 'bg-gray-300' : 'bg-gray-100'}`}></div>
                                    )}

                                    {/* Burbuja */}
                                    <div className={`
                                        z-10 w-9 h-9 rounded-full flex items-center justify-center mb-3 shadow-sm transition-all
                                        ${isCompleted ? 'bg-green-500 text-white' : isCurrent ? 'bg-white border-4 border-green-500 text-green-600 scale-110 shadow-lg' : isSkipped ? 'bg-gray-300 text-white' : 'bg-white border-2 border-gray-200 text-gray-300'}
                                    `}>
                                        {isCompleted
                                            ? <span className="material-icons text-sm">check</span>
                                            : isSkipped
                                                ? <span className="material-icons text-sm">block</span>
                                                : <span className="text-xs font-bold">{index + 1}</span>
                                        }
                                    </div>

                                    <div className={`text-center transition-all ${isCurrent ? 'scale-105' : ''}`}>
                                        {/* Nombre */}
                                        <div className={`text-[10px] font-black uppercase mb-1 ${isCurrent ? 'text-green-600' : isCompleted ? 'text-gray-600' : isSkipped ? 'text-gray-500' : 'text-gray-400'}`}>
                                            {phase.phaseName}
                                        </div>

                                        {isSkipped ? (
                                            <div className="space-y-1">
                                                <div className="text-[9px] text-gray-500 font-bold">
                                                    Omitida — cierre anticipado del ciclo
                                                </div>
                                                {phase.actualCompletionDate && (
                                                    <div className="text-[8px] text-gray-500 font-bold uppercase">
                                                        {phase.actualCompletionDate}
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="space-y-1">
                                                {/* Métrica de cierre de la fase — GDD, CHILL, DAYS o MANUAL */}
                                                <div className="text-[9px] text-gray-400 font-bold">
                                                    {(() => {
                                                        switch (phase.completionMetric) {
                                                            case 'CHILL':
                                                                return isCompleted
                                                                    ? `${Math.round(phase.chillAtCompletion || 0)} CHP (Fin)`
                                                                    : `Objetivo: ${phase.targetChill ?? '—'} CHP`;
                                                            case 'DAYS':
                                                                return isCompleted
                                                                    ? 'Completada por días'
                                                                    : `Objetivo: ${phase.expectedDaysSnapshot ?? '—'} días`;
                                                            case 'MANUAL':
                                                                return isCompleted ? 'Completada manualmente' : 'Cierre manual';
                                                            default:
                                                                return isCompleted
                                                                    ? `${Math.round(phase.gddAtCompletion || 0)} GDD (Fin)`
                                                                    : `Objetivo: ${phase.targetGdd} GDD`;
                                                        }
                                                    })()}
                                                </div>

                                                {/* Fecha real si está completada */}
                                                {isCompleted && phase.actualCompletionDate && (
                                                    <div className="text-[8px] text-green-600 font-bold uppercase">
                                                        {phase.actualCompletionDate}
                                                    </div>
                                                )}

                                                {/* Solo fecha estimada central para actuales/futuras — sin rango ni por-días */}
                                                {(isCurrent || isFuture) && phase.estimatedCompletionDate && (
                                                    <div className="text-[8px] text-indigo-500 font-bold uppercase">
                                                        Estimado: {phase.estimatedCompletionDate}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Badge estado */}
                                        {isCurrent && (
                                            <div className="mt-2 px-2 py-0.5 bg-green-50 text-green-600 rounded text-[9px] font-black animate-pulse border border-green-100">
                                                EN CURSO
                                            </div>
                                        )}

                                        {isCompleted && (
                                            <div className={`mt-1 px-2 py-0.5 rounded text-[8px] font-black border ${
                                                phase.isAutoCompleted
                                                    ? 'bg-blue-50 text-blue-500 border-blue-100'
                                                    : 'bg-orange-50 text-orange-500 border-orange-100'
                                            }`}>
                                                {phase.isAutoCompleted ? 'SISTEMA' : 'MANUAL'}
                                            </div>
                                        )}

                                        {isSkipped && (
                                            <div className="mt-1 px-2 py-0.5 rounded text-[8px] font-black border bg-gray-100 text-gray-500 border-gray-200">
                                                OMITIDA
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Card.Body>
            </Card>

        

            <ConfirmPhaseModal
                show={showConfirmModal}
                stageName={plantingCycle.currentStage}
                isLoading={forcingStage}
                onConfirm={handleForceNextStage}
                onCancel={() => setShowConfirmModal(false)}
            />

            <RegisterHarvestModal
                show={showHarvestModal}
                isMultiple={isMultiple}
                previousHarvestCount={harvests.length}
                existingHarvest={editingHarvest}
                isLoading={registeringHarvest}
                onConfirm={handleSaveHarvest}
                onCancel={() => { setShowHarvestModal(false); setEditingHarvest(null); }}
            />

            <FinishCycleModal
                show={showFinishModal}
                harvests={harvests}
                isLoading={finishingCycle}
                onConfirm={handleFinishCycle}
                onCancel={() => setShowFinishModal(false)}
            />

            <AbandonCycleModal
                show={showAbandonModal}
                isLoading={abandoningCycle}
                onConfirm={handleAbandonCycle}
                onCancel={() => setShowAbandonModal(false)}
            />
        </div>
    );
};

export default CycleStatus;
